import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Background } from "../components/Background";
import { Chat } from "../components/Chat";
import { Countdown } from "../components/Countdown";
import { GameCanvas } from "../components/GameCanvas";
import { Button } from "../components/ui/Button";
import { useSocket } from "../state/SocketContext";

export default function MultiplayerGame() {
  const navigate = useNavigate();
  const {
    matchStart,
    matchOver,
    scores,
    reportDeath,
    socketId,
    opponentConnectivity,
    leaveRoom,
  } = useSocket();
  const [diedReported, setDiedReported] = useState(false);

  // Redirect away if there is no active match (e.g. user came here directly).
  useEffect(() => {
    if (!matchStart) {
      const t = window.setTimeout(() => {
        if (!matchStart) navigate("/");
      }, 400);
      return () => window.clearTimeout(t);
    }
    return undefined;
  }, [matchStart, navigate]);

  // Navigate to results when the server declares the match over.
  useEffect(() => {
    if (matchOver) {
      const t = window.setTimeout(() => navigate("/results"), 600);
      return () => window.clearTimeout(t);
    }
    return undefined;
  }, [matchOver, navigate]);

  const onGameOver = useCallback(() => {
    if (!diedReported) {
      reportDeath();
      setDiedReported(true);
    }
  }, [diedReported, reportDeath]);

  const selfScore = socketId ? scores?.scores[socketId] : null;
  const opponentEntry = useMemo(() => {
    if (!scores || !socketId) return null;
    const entry = Object.entries(scores.scores).find(([id]) => id !== socketId);
    if (!entry) return null;
    return entry[1];
  }, [scores, socketId]);

  const selfName =
    matchStart?.players.find((p) => p.socketId === socketId)?.name ?? "You";
  const countdownSec = matchStart?.countdownSec ?? 3;

  if (!matchStart) {
    return (
      <Background>
        <div className="flex min-h-screen items-center justify-center">
          <p className="text-sm uppercase tracking-widest text-white/60">Joining mission…</p>
        </div>
      </Background>
    );
  }

  return (
    <Background>
      <div className="mx-auto flex min-h-screen max-w-[1400px] flex-col gap-4 p-3 sm:p-6">
        <div className="flex items-center justify-between gap-3">
          <div className="font-display text-xl uppercase tracking-[0.3em] text-white/80">
            Co-op Mission
          </div>
          <div className="flex items-center gap-3">
            {opponentConnectivity === "lost" && (
              <span className="rounded-full bg-cap-red/20 px-3 py-1 text-xs uppercase tracking-widest text-cap-red">
                Opponent dropped — waiting…
              </span>
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={async () => {
                await leaveRoom();
                navigate("/");
              }}
            >
              Forfeit
            </Button>
          </div>
        </div>

        <div className="relative flex-1">
          <GameCanvas
            levelConfig={matchStart.level}
            selfName={selfName}
            opponent={
              opponentEntry
                ? {
                    name: opponentEntry.name,
                    survivedMs: opponentEntry.survivedMs,
                    alive: opponentEntry.alive,
                  }
                : null
            }
            onGameOver={onGameOver}
            startDelayMs={countdownSec * 1000}
          />
          <Countdown seconds={countdownSec} />
          {matchOver && (
            <div className="absolute inset-0 z-30 flex items-center justify-center bg-cap-deep/85 backdrop-blur-sm">
              <div className="text-center">
                <div className="font-display text-xs uppercase tracking-[0.4em] text-cap-gold">
                  Match Over
                </div>
                <div className="mt-2 font-display text-5xl text-white">
                  Compiling results…
                </div>
              </div>
            </div>
          )}
        </div>

        {selfScore && (
          <div className="text-center text-xs uppercase tracking-widest text-white/40">
            Server-tracked survival ·{" "}
            {(selfScore.survivedMs / 1000).toFixed(1)}s
          </div>
        )}
      </div>
      <Chat />
    </Background>
  );
}
